import { Button, ButtonType } from '@UI/components/controls/Button/Button.tsx'
import { ArrowChevronDown } from '@UI/components/icons/ArrowChevronDown.tsx'
import { Checked } from '@UI/components/icons/Checked.tsx'
import { SaveIcon } from '@UI/components/icons/SaveIcon.tsx'
import { FiltersIllustration } from '@UI/illustrationsSvg/FiltersIllustration.tsx'
import { ColorNames } from '@UI/theme/generated/ColorNames.ts'
import {
    Body_14_Coral_Empasis_14,
    Body_14_Coral_Strong_emphasis_14,
    Title_20_Coral_Strong_emphasis_20
} from '@UI/typography/generated/typography.tsx'
import { Accordion } from '@src/CommonComponents/Accordion/Accordion.tsx'
import styles from '@src/CommonComponents/FiltersButton/button.module.less'
import { Stack } from '@src/CommonComponents/Stack/Stack.tsx'
import { useModalStore } from '@src/Layers/ModalLayer/ModalStore.ts'
import { useCredentialsStore } from '@src/Store/userStore.ts'
import { Gt } from '@src/i18n/Gt.tsx'
import { BRANCH_FILTER_KEY, IS_SHIFTED_FILTER_KEY, useFilters } from '@src/utils/useFilters.ts'
import { apiClient } from '@src/httpClient/api.ts'
import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

const BranchesFilter = ({
    addFilter,
    filters
}: {
    addFilter: (key: string, value: string) => void
    filters: Record<string, string[]>
}) => {
    const employee = useCredentialsStore(x => x.employee)
    // Staff are scoped to every branch server-side, not to the ones they work at.
    const { data: allBranches } = useQuery({
        queryKey: ['branches-all'],
        queryFn: async () => (await apiClient.api.companyBranchesList(undefined, { search: 'page_size=200' })).data.results,
        enabled: !!employee?.is_staff
    })
    const branches = employee?.is_staff ? allBranches : employee?.branches
    const filterValues = useMemo(() => {
        return filters[BRANCH_FILTER_KEY] ?? []
    }, [filters])

    const elements = useMemo(() => {
        const priority = new Set(filterValues.map(String))

        return [...(branches ?? [])].sort((a, b) => {
            const aIn = priority.has(String(a.id))
            const bIn = priority.has(String(b.id))

            if (aIn === bIn) return 0
            return aIn ? -1 : 1
        })
    }, [branches, filterValues])
    if (!branches) return null

    return (
        <Stack direction={'column'} alignItems={'flex-start'}>
            <Stack direction={'column'} className={styles.filtersBranches}>
                {elements?.map(branch => (
                    <Stack
                        className={cn(styles.branchElem, {
                            [styles.branchElemActive]: filterValues.includes(branch?.id.toString())
                        })}
                        alignItems={'center'}
                        key={branch.id}
                        justifyContent={'space-between'}
                        onClick={() => addFilter(BRANCH_FILTER_KEY, branch?.id.toString())}
                    >
                        <Body_14_Coral_Strong_emphasis_14>{branch?.name}</Body_14_Coral_Strong_emphasis_14>
                        {filterValues.includes(branch?.id.toString()) && <Checked size={18} />}
                    </Stack>
                ))}
            </Stack>
        </Stack>
    )
}

const STATUS_OPTIONS = [
    { key: IS_SHIFTED_FILTER_KEY, label: 'Shifted' }
] as const

const UsersFilter = ({
    addFilter,
    filters
}: {
    addFilter: (key: string, value: string) => void
    filters: Record<string, string[]>
}) => {
    return (
        <Stack direction={'column'} alignItems={'flex-start'}>
            <Stack direction={'column'} className={styles.filtersBranches}>
                {STATUS_OPTIONS.map(({ key, label }) => {
                    const active = (filters[key] ?? []).includes('true')
                    return (
                        <Stack
                            className={cn(styles.branchElem, { [styles.branchElemActive]: active })}
                            alignItems={'center'}
                            key={key}
                            justifyContent={'space-between'}
                            onClick={() => addFilter(key, 'true')}
                        >
                            <Body_14_Coral_Strong_emphasis_14>
                                <Gt>{label}</Gt>
                            </Body_14_Coral_Strong_emphasis_14>
                            {active && <Checked size={18} />}
                        </Stack>
                    )
                })}
            </Stack>
        </Stack>
    )
}

interface AccordionFilterProps extends React.PropsWithChildren {
    title: string
    counter?: number
}

const AccordionFilters: React.FC<AccordionFilterProps> = ({ children, title, counter = 0 }) => {
    return (
        <Accordion
            initialOpened={false}
            button={({ toggle }) => (
                <Stack
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    className={styles.filtersGroup}
                    onClick={() => toggle()}
                    style={{ cursor: 'pointer' }}
                >
                    <Body_14_Coral_Strong_emphasis_14>
                        <Gt>{title}</Gt>
                        {counter > 0 && <span> ({counter})</span>}
                    </Body_14_Coral_Strong_emphasis_14>
                    <ArrowChevronDown size={16} />
                </Stack>
            )}
        >
            {children}
        </Accordion>
    )
}

export const FiltersModal = () => {
    const reset = useModalStore(x => x.reset)
    const { filters, addFilter, applyFilters } = useFilters()
    // The schedule calendar has no shift status to filter by.
    const onCalendar = useLocation().pathname.startsWith('/calendar')

    return (
        <Stack
            grow
            alignItems={'center'}
            justifyContent={'center'}
            direction={'column'}
            gap={16}
            className={styles.filtersModal}
        >
            <Stack direction={'column'} alignItems={'center'}>
                <FiltersIllustration />
                <Title_20_Coral_Strong_emphasis_20>
                    <Gt>Advanced Filters</Gt>
                </Title_20_Coral_Strong_emphasis_20>
            </Stack>

            <AccordionFilters title='By store' counter={filters?.branches?.length ?? 0}>
                <BranchesFilter addFilter={addFilter} filters={filters} />
            </AccordionFilters>

            {!onCalendar && (
                <AccordionFilters
                    title='By status'
                    counter={filters[IS_SHIFTED_FILTER_KEY]?.length ?? 0}
                >
                    <UsersFilter addFilter={addFilter} filters={filters} />
                </AccordionFilters>
            )}

            <Button
                type={ButtonType.primary}
                icon={<SaveIcon />}
                color={ColorNames.CORAL_MOSS}
                onClick={() => {
                    applyFilters()
                    reset()
                }}
                className={styles.popupButton}
            >
                <Body_14_Coral_Empasis_14>
                    <Gt>Confirmed</Gt>
                </Body_14_Coral_Empasis_14>
            </Button>
        </Stack>
    )
}
